import { EmailRounded, PhoneRounded, RateReviewRounded, SmsRounded, TagOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CRMMenu from "../../../App/Components/Menu/CRMMenu";
import { AppFeatureLookup, defaultUnauthorizedMessage } from "../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useMessageDialogHandlers } from "../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { ICandidate } from "../../../App/Typings/Candidate";
import { IMessageContainers } from "../../../MessageComponents/Enums/MessageEnums";

export function CandidateActions({
  candidate,
  setSelectedCandidate,
  onEditTagsClick,
}: {
  candidate: ICandidate;
  setSelectedCandidate: React.Dispatch<React.SetStateAction<ICandidate | undefined>>;
  onEditTagsClick: (candidate: ICandidate) => void;
}) {
  const navigate = useNavigate();
  const { handleOpenDialog } = useMessageDialogHandlers();
  const viewCandidateAccess = useFeaturePermission(AppFeatureLookup.CandidatesViewSingleCandidate);
  const emailDirectAccess = useFeaturePermission(AppFeatureLookup.CandidatesEmailCandidateAction);
  const textDirectAccess = useFeaturePermission(AppFeatureLookup.CandidatesTextCandidateAction);
  const callDirectAccess = useFeaturePermission(AppFeatureLookup.CandidatesCallCandidateAction);

  const candidatePhone = candidate.phoneNumbers.find((phoneNumber) => phoneNumber.isValid);
  const hasPhone = candidatePhone !== undefined;
  const candidateEmail = candidate.emailAddresses.find(
    (email) => (email.validationAttempt && email.isValid) || !email.validationAttempt,
  );
  const hasEmail = candidateEmail !== undefined;

  const viewCandidateMessaging = viewCandidateAccess
    ? `Write a note for ${candidate.firstName}`
    : defaultUnauthorizedMessage;
  const emailDirectMessaging = emailDirectAccess
    ? hasEmail
      ? "Email candidate directly."
      : "Candidate does not have an email address"
    : crmLiteUpgradeMessage;
  const callDirectMessaging = callDirectAccess
    ? hasPhone
      ? `Call ${candidate.firstName}`
      : "Candidate does not have a valid phone number"
    : crmLiteUpgradeMessage;
  const textDirectMessaging = textDirectAccess
    ? hasPhone
      ? "Send text message directly to candidate."
      : "Candidate does not have a valid phone number"
    : crmLiteUpgradeMessage;

  const candidateIds = [candidate.id];
  const candidateEntities = {
    [candidate.id]: {
      candidateId: candidate.id,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      hasEmail,
      hasPhone,
      unsubscribed: candidate.unsubscribed,
      emailAddress: hasEmail ? candidateEmail?.emailAddress : undefined,
      phoneNumber: hasPhone ? candidatePhone?.phoneNumber : undefined,
    },
  };

  const handleOpenSendTextMessageIcon = () => {
    if (!textDirectAccess || !hasPhone) {
      return;
    }
    setSelectedCandidate(candidate);
    handleOpenDialog({
      isCandidateTextOpen: true,
      containerType: IMessageContainers.CandidateTextNow,
      candidateEntities,
      candidateIds,
    });
  };
  const handleOpenSendEmailMessageIcon = () => {
    if (!emailDirectAccess || !hasEmail) {
      return;
    }
    setSelectedCandidate((_) => candidate);
    handleOpenDialog({
      isCandidateEmailOpen: true,
      containerType: IMessageContainers.CandidateEmailNow,
      candidateEntities,
      candidateIds,
    });
  };

  return (
    <CRMMenu
      title="Candidate Quick Actions"
      id={`candidate-quick-actions-${candidate.id}`}
      links={[
        {
          dataTestId: `management-candidate-actions-notes-${candidate.id}`,
          title: "Add a note",
          disabled: !viewCandidateAccess,
          tooltip: viewCandidateMessaging,
          icon: <RateReviewRounded sx={{ fontSize: "1rem" }} />,
          onClick: (_ev) => {
            if (viewCandidateAccess) {
              navigate(`/candidates/${candidate.id}`);
            }
          },
        },
        {
          dataTestId: `management-candidate-actions-email-${candidate.id}`,
          title: "Email Candidate",
          disabled: !emailDirectAccess || !hasEmail,
          tooltip: emailDirectMessaging,
          icon: <EmailRounded sx={{ fontSize: "1rem" }} />,
          onClick: (_ev) => {
            if (emailDirectAccess) {
              handleOpenSendEmailMessageIcon();
            }
          },
        },
        {
          dataTestId: `management-candidate-actions-call-${candidate.id}`,
          title: "Call Candidate",
          disabled: !callDirectAccess || !hasPhone,
          tooltip: callDirectMessaging,
          icon: <PhoneRounded sx={{ fontSize: "1rem" }} />,
          href: callDirectAccess && hasPhone ? `tel:${candidatePhone?.phoneNumber}` : undefined,
          isLink: true,
        },
        {
          dataTestId: `management-candidate-actions-text-${candidate.id}`,
          title: "Text Message Candidate",
          disabled: !textDirectAccess || !hasPhone,
          tooltip: textDirectMessaging,
          icon: <SmsRounded sx={{ fontSize: "1rem" }} />,
          onClick: (_ev) => {
            handleOpenSendTextMessageIcon();
          },
        },
        {
          dataTestId: `management-candidate-actions-tags-${candidate.id}`,
          title: "Edit candidate tags",
          tooltip: "Edit candidate tags",
          icon: <TagOutlined sx={{ fontSize: "1rem" }} />,
          onClick: (_ev) => {
            onEditTagsClick(candidate);
          },
        },
      ]}
    />
  );
}
